import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../Utils/apiClient';
import FolderBrowser from '../FolderBrowser';
import {
  buildStorageReference,
  getStorageFileName,
  getStorageReferenceLabel,
} from './storageMentionUtils';

export { buildStorageReference, getStorageFileName, getStorageReferenceLabel };

const StorageMentionPicker = forwardRef(({
  selectedRefs = [],
  onSelect,
  onRemove,
  disabled = false,
  compact = false,
  showSelected = true,
  showTriggerButton = true,
}, ref) => {
  const { t } = useTranslation();
  const [browserOpen, setBrowserOpen] = useState(false);
  const [storageStatus, setStorageStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const open = useCallback(async () => {
    if (disabled) return;
    setErrorMessage('');
    if (storageStatus) {
      setBrowserOpen(true);
      return;
    }
    if (loadingStatus) return;
    setLoadingStatus(true);
    try {
      const payload = await apiRequest('/api/v1/storage/status');
      setStorageStatus(payload || {});
      setBrowserOpen(true);
    } catch (error) {
      setErrorMessage(error?.message || t('storage_mention.error'));
    } finally {
      setLoadingStatus(false);
    }
  }, [disabled, loadingStatus, storageStatus, t]);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const handleFileSelect = (path) => {
    const reference = buildStorageReference(path);
    if (reference.path) {
      onSelect?.(reference);
    }
    setBrowserOpen(false);
  };

  return (
    <Box sx={{ mt: compact ? 0 : 1 }}>
      {showTriggerButton && (
        <Tooltip title={t('storage_mention.button_tooltip')}>
          <span>
            <Button
              type="button"
              size={compact ? 'small' : 'medium'}
              variant="outlined"
              startIcon={loadingStatus ? <CircularProgress size={16} /> : <AttachFileIcon fontSize="small" />}
              onClick={open}
              disabled={disabled || loadingStatus}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              {t('storage_mention.button')}
            </Button>
          </span>
        </Tooltip>
      )}
      {errorMessage && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {errorMessage}
        </Typography>
      )}
      {showSelected && selectedRefs.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {selectedRefs.map((reference) => (
            <Chip
              key={reference.path}
              size="small"
              icon={<AttachFileIcon />}
              label={reference.label || getStorageReferenceLabel(reference.path)}
              onDelete={onRemove ? () => onRemove(reference.path) : undefined}
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Stack>
      )}
      <FolderBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelect={handleFileSelect}
        restrictToUserFolder={Boolean(storageStatus?.restrict_to_user_folder ?? storageStatus?.restrictToUserFolder)}
        allowedRoots={Array.isArray(storageStatus?.allowedRoots) ? storageStatus.allowedRoots : []}
        allowFileSelection
      />
    </Box>
  );
});

export default StorageMentionPicker;
